/*
  Japyara "Help me choose" product finder (snippets/jp-assistant.liquid)
  ------------------------------------------------------------------
  - Opens the dialog from any link ending in #help-me-choose (and on load when the URL has that hash).
  - Searches with Shopify's predictive search (routes.predictive_search_url + .json, the same endpoint as
    the header search, so it follows the store's language and search settings). No AI, no other service.
  - KEYWORD_MAP: when a customer uses a common equivalent of the store's wording (e.g. "everyday worship"
    -> "daily puja kit"), the rewritten sentence is searched too. Both searches are shown to the customer.
  - Only products returned by Shopify are shown, with Shopify's own title, image, price and URL.
  - Cards only link to the product page: no add to cart or subscription from here.
*/
(function () {
  const dialog = document.querySelector('[data-jp-assistant]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  /*
    Common wording -> the words used in the Japyara catalogue. Keys are matched as whole words/phrases,
    case-insensitive. Each value is searched in addition to what the customer typed. Keep entries short
    and only add terms that exist in product titles, types or tags.
  */
  const KEYWORD_MAP = {
    'everyday worship': 'daily puja kit',
    'everyday puja': 'daily puja kit',
    'daily worship': 'daily puja kit',
    'regular puja': 'daily puja kit',
    'nitya puja': 'daily puja kit',
    'festival worship': 'festival puja kit',
    'festival puja': 'festival puja kit',
    'festive puja': 'festival puja kit',
    'utsav': 'festival puja kit',
    'deepavali': 'diwali',
    'pooja': 'puja',
    'agarbatti': 'dhoop',
    'incense': 'dhoop',
    'sambrani': 'sambrani cup',
    'lamp': 'diya',
    'wicks': 'cotton wicks',
    'batti': 'cotton wicks',
    'camphor': 'kapoor',
    'sandalwood': 'chandan',
    'roli': 'kumkum',
    'holy water': 'gangajal',
    'idol': 'murti',
    'statue': 'murti',
    'photo frame': 'framed picture',
    'god photo': 'framed picture',
    'gift': 'devotional gift',
  };

  const TRIGGER_HASH = '#help-me-choose';
  const MAX_RESULTS = 6;
  const PER_SEARCH = 10;
  const TIMEOUT_MS = 12000;

  const suggestUrl = (dialog.dataset.predictiveSearchUrl || '/search/suggest') + '.json';
  const searchUrl = dialog.dataset.searchUrl || '/search';
  const kitsUrl = dialog.dataset.kitsUrl || '/collections/puja-kits';

  const form = dialog.querySelector('[data-jp-assistant-form]');
  const input = dialog.querySelector('[data-jp-assistant-input]');
  const error = dialog.querySelector('[data-jp-assistant-error]');
  const submit = dialog.querySelector('[data-jp-assistant-submit]');
  const status = dialog.querySelector('[data-jp-assistant-status]');
  const results = dialog.querySelector('[data-jp-assistant-results]');
  const again = dialog.querySelector('[data-jp-assistant-again]');
  const body = dialog.querySelector('[data-jp-assistant-body]');

  let opener = null;
  let request = null;
  let lastQuery = '';

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* Shopify money format ({{amount}}, {{amount_no_decimals}}, ...) using the theme's own format */
  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '{{amount}}';
    const group = (value, decimals, thousands, decimal) => {
      const parts = (value / 100).toFixed(decimals).split('.');
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(cents, 2, "'", '.');
        case 'amount_with_space_separator': return group(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(cents, 0, ' ', ',');
        default: return group(cents, 2, ',', '.');
      }
    });
  }

  // Predictive search returns prices as decimal strings ("201.00") in the customer's currency
  const toCents = (value) => Math.round(parseFloat(value || '0') * 100);

  /* ---------- Open / close ---------- */
  function open(trigger) {
    opener = trigger || document.activeElement;
    if (!dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('jp-assistant-open');
    }
    if (!form.hidden) input.focus();
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('jp-assistant-open');
    if (request) request.abort();
    if (opener && typeof opener.focus === 'function') opener.focus();
    opener = null;
  });

  dialog.querySelector('[data-jp-assistant-close]').addEventListener('click', close);

  // Click on the backdrop (outside the panel) closes
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  // Capture phase so the theme's own in-page link handling never sees these clicks
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest && event.target.closest('a[href]');
      if (!link || !link.getAttribute('href').endsWith(TRIGGER_HASH)) return;
      event.preventDefault();
      open(link);
    },
    true
  );

  if (window.location.hash === TRIGGER_HASH) open(null);

  /* ---------- States ---------- */
  function setError(message) {
    error.textContent = message;
    error.hidden = !message;
    if (message) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  function setLoading(loading) {
    form.classList.toggle('is-loading', loading);
    submit.disabled = loading;
    input.readOnly = loading;
    dialog.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function showStatus(html, tone) {
    status.className = 'jp-assistant__status' + (tone ? ' jp-assistant__status--' + tone : '');
    status.innerHTML = html;
  }

  function showAnswerState() {
    form.hidden = true;
    again.hidden = false;
  }

  // Keyboard / screen reader users continue from the answer's first line into the products
  function focusAnswer() {
    body.scrollTop = 0;
    status.focus({ preventScroll: true });
  }

  function reset() {
    if (request) request.abort();
    setLoading(false);
    setError('');
    showStatus('');
    results.innerHTML = '';
    results.hidden = true;
    again.hidden = true;
    form.hidden = false;
    input.value = '';
    body.scrollTop = 0;
    input.focus();
  }

  again.addEventListener('click', reset);

  // "Try again" inside an error message repeats the last search
  status.addEventListener('click', (event) => {
    if (event.target.closest('[data-jp-assistant-retry]')) run(lastQuery);
  });

  dialog.querySelectorAll('[data-jp-assistant-example]').forEach((chip) => {
    chip.addEventListener('click', () => {
      input.value = chip.textContent.trim();
      setError('');
      input.focus();
    });
  });

  input.addEventListener('input', () => {
    if (!error.hidden && input.value.trim()) setError('');
  });

  // Enter sends, Shift+Enter adds a new line
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      form.requestSubmit();
    }
  });

  const browseLinks = (query) =>
    '<p class="jp-assistant__fallback">' +
    '<a href="' + escapeHtml(kitsUrl) + '">Explore all puja kits</a>' +
    '<a href="' + escapeHtml(searchUrl + '?q=' + encodeURIComponent(query)) + '">See all search results</a>' +
    '</p>';

  /* ---------- Search ---------- */

  // The customer's words with KEYWORD_MAP phrases swapped for catalogue wording (whole words only).
  // Returns '' when nothing was swapped.
  function rewrite(query) {
    const original = ' ' + query.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim() + ' ';
    let text = original;
    // Longest phrases first, so "festival puja" wins over "puja"-level entries
    Object.keys(KEYWORD_MAP)
      .sort((a, b) => b.length - a.length)
      .forEach((phrase) => {
        const term = KEYWORD_MAP[phrase];
        if (text.includes(' ' + phrase + ' ') && !text.includes(' ' + term + ' ')) text = text.split(' ' + phrase + ' ').join(' ' + term + ' ');
      });
    return text === original ? '' : text.trim();
  }

  async function predictiveSearch(term, signal) {
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product',
      'resources[limit]': String(PER_SEARCH),
      'resources[options][unavailable_products]': 'last',
    });
    const response = await fetch(suggestUrl + '?' + params.toString(), { signal, headers: { Accept: 'application/json' } });
    if (response.status === 429) throw new Error('busy');
    if (!response.ok) throw new Error('http');
    const data = await response.json();
    return (data.resources && data.resources.results && data.resources.results.products) || [];
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const query = input.value.trim();
    if (!query) {
      setError('Please tell me what you are looking for, for example “daily puja kit” or “dhoop”.');
      input.focus();
      return;
    }
    setError('');
    run(query);
  });

  async function run(query) {
    lastQuery = query;
    if (request) request.abort();
    request = new AbortController();
    const controller = request;
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    const rewritten = rewrite(query);

    form.hidden = false;
    again.hidden = true;
    results.innerHTML = '';
    results.hidden = true;
    setLoading(true);
    showStatus('<p>Let me find something that matches your needs…</p>');

    try {
      // The catalogue-wording version first (it names what the customer described), then their own words
      const terms = rewritten ? [rewritten, query] : [query];
      const lists = await Promise.all(terms.map((term) => predictiveSearch(term, controller.signal)));
      const seen = new Set();
      const products = [];
      lists.forEach((list) =>
        list.forEach((product) => {
          if (!product || !product.handle || seen.has(product.handle)) return;
          seen.add(product.handle);
          products.push(product);
        })
      );
      render(products.slice(0, MAX_RESULTS), query, rewritten);
    } catch (err) {
      if (!dialog.open || controller !== request) return;
      const message =
        err.name === 'AbortError'
          ? 'Sorry, the search is taking longer than expected.'
          : err.message === 'busy'
            ? 'Sorry, the search is busy right now.'
            : 'Sorry, I couldn’t search the shop just now.';
      showStatus(
        '<p class="jp-assistant__status-title">' + message + '</p>' +
          '<p>Please check your connection and try again.</p>' +
          '<p class="jp-assistant__fallback"><button type="button" class="jp-assistant__retry" data-jp-assistant-retry>Try again</button>' +
          '<a href="' + escapeHtml(kitsUrl) + '">Explore all puja kits</a></p>',
        'error'
      );
      showAnswerState();
      focusAnswer();
    } finally {
      clearTimeout(timer);
      if (controller === request) {
        setLoading(false);
        request = null;
      }
    }
  }

  function card(product) {
    const href = product.url || '';
    const image = (product.featured_image && product.featured_image.url) || product.image || '';
    const src = image ? image + (image.includes('?') ? '&' : '?') + 'width=240' : '';
    const min = toCents(product.price_min || product.price);
    const max = toCents(product.price_max || product.price);
    const compare = toCents(product.compare_at_price_min);
    const price = max > min ? 'From ' + formatMoney(min) : formatMoney(min);
    const compareHtml =
      max === min && compare > min
        ? ' <s class="jp-assistant__compare"><span class="visually-hidden">Regular price </span>' + formatMoney(compare) + '</s>'
        : '';

    return (
      '<li class="jp-assistant__card">' +
      '<a class="jp-assistant__media" href="' + escapeHtml(href) + '" tabindex="-1" aria-hidden="true">' +
      (src ? '<img src="' + escapeHtml(src) + '" alt="" width="120" height="120" loading="lazy">' : '') +
      '</a>' +
      '<div class="jp-assistant__info">' +
      '<h3 class="jp-assistant__name">' + escapeHtml(product.title) + '</h3>' +
      '<p class="jp-assistant__price">' + escapeHtml(price) + compareHtml + (product.available === false ? ' <span class="jp-assistant__soldout">Sold out</span>' : '') + '</p>' +
      '<a class="jp-assistant__view" href="' + escapeHtml(href) + '">View product<span class="visually-hidden">: ' + escapeHtml(product.title) + '</span></a>' +
      '</div>' +
      '</li>'
    );
  }

  function render(products, query, rewritten) {
    const searched =
      '<p class="jp-assistant__searched">Searched for “' + escapeHtml(query) + '”' +
      (rewritten ? ' and “' + escapeHtml(rewritten) + '”' : '') + '</p>';

    if (!products.length) {
      showStatus(
        '<p class="jp-assistant__status-title">I couldn’t find an exact match. Try different keywords or explore all our puja kits.</p>' +
          searched +
          browseLinks(query),
        'info'
      );
    } else {
      showStatus('<p class="jp-assistant__status-title">I’ve found a few options you may like.</p>' + searched);
      results.innerHTML = products.map(card).join('');
      results.hidden = false;
    }
    showAnswerState();
    focusAnswer();
  }
})();
